import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import type { PaymentGateway, PaymentMethod } from "types";
import {
  createStripePaymentIntent,
  fetchPaymentGatewayBySupplier,
  fetchPaymentMethodsBySupplierAndUser,
  resolveSupplierOrganizationId,
} from "api/salesOrderApi";

export type SelectedCheckoutPaymentMethod = {
  name: string;
  _id: string;
};

export type UseCheckoutPaymentParams = {
  token: string;
  supplierId: string;
  payerUserId: string;
  orderId: string;
  totalAmount: number;
  enabled: boolean;
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  order?: { seller_org_id?: string; signage_id?: string } | null;
};

export function useCheckoutPayment({
  token,
  supplierId,
  payerUserId,
  orderId,
  totalAmount,
  enabled,
  orderNumber,
  customerName,
  customerEmail,
  customerPhone,
  order,
}: UseCheckoutPaymentParams) {
  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);
  const [dynamicMethods, setDynamicMethods] = useState<PaymentMethod[]>([]);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<SelectedCheckoutPaymentMethod>({ name: "online", _id: "online" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !token || !supplierId) return;

    const organizationId = resolveSupplierOrganizationId(
      supplierId || order?.signage_id,
      order
    );
    if (!organizationId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchPaymentGatewayBySupplier(token, organizationId);
        if (!cancelled) setIntegrations(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setIntegrations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, token, supplierId, order]);

  useEffect(() => {
    const stripeInfo = integrations.find((item) => item.gateway === "stripe");
    if (stripeInfo?.publishable_key) {
      setStripePromise(loadStripe(stripeInfo.publishable_key));
    } else {
      setStripePromise(null);
    }
  }, [integrations]);

  useEffect(() => {
    if (!enabled || !token || !supplierId || !payerUserId) return;

    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPaymentMethodsBySupplierAndUser(
          token,
          supplierId,
          payerUserId
        );
        if (!cancelled) setDynamicMethods(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setDynamicMethods([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, token, supplierId, payerUserId]);

  useEffect(() => {
    if (!enabled || !stripePromise || !supplierId || !orderId || totalAmount <= 0) {
      setClientSecret("");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const organizationId = resolveSupplierOrganizationId(
          supplierId || order?.signage_id,
          order
        );
        const data = await createStripePaymentIntent(token, organizationId, {
          amount: Math.round(Number(totalAmount) * 100),
          currency: "usd",
          order_id: orderId,
          order_number: String(orderNumber || ""),
          customer_name: String(customerName || ""),
          customer_email: String(customerEmail || ""),
          customer_phone: String(customerPhone || ""),
          payment_method_types: ["card"],
        });
        if (!cancelled) {
          setClientSecret(String(data.client_secret || data.clientSecret || ""));
        }
      } catch {
        if (!cancelled) setClientSecret("");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, stripePromise, supplierId, orderId, totalAmount, token, orderNumber, customerName, customerEmail, customerPhone, order]);

  return {
    dynamicMethods,
    stripePromise,
    clientSecret,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    loading,
  };
}
